/**
 * FireballLatest — displays the most recent Illinois Pick 3 Fireball digit.
 * Auto-refreshes every 60 s so it stays fresh across midday / evening draws.
 * The winning digit is the big colored numeral; underneath is the draw type
 * (midday / evening) and the date. Falls back gracefully if no draw yet.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Flame, Sun, Moon, RefreshCw, Clock, Zap } from 'lucide-react';

const REFRESH_MS = 60_000;
const COUNTDOWN_TICK_MS = 1_000;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso.length <= 10 ? `${iso}T12:00:00Z` : iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return iso; }
};

const formatCountdown = (msLeft) => {
  if (msLeft <= 0) return 'drawing…';
  const total = Math.floor(msLeft / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

export const FireballLatest = ({ onQuickBuy, onUrgentChange } = {}) => {
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  const fetchLatest = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const [rl, rs] = await Promise.all([
        axios.get(`${API}/lottery/results?limit=1`),
        axios.get(`${API}/draws/schedule`).catch(() => ({ data: null })),
      ]);
      const arr = rl.data?.results || [];
      setLatest(arr[0] || null);
      setSchedule(rs.data || null);
    } catch { /* silent */ }
    finally {
      setLoading(false);
      if (isRefresh) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchLatest();
    const dataIv = setInterval(() => fetchLatest(true), REFRESH_MS);
    const tickIv = setInterval(() => setNow(Date.now()), COUNTDOWN_TICK_MS);
    return () => { clearInterval(dataIv); clearInterval(tickIv); };
  }, [fetchLatest]);

  const digit = latest?.line3_fireball;
  const isMidday = latest?.draw_type === 'midday';

  // Compute next draw (whichever is soonest).
  let nextDraw = null;
  if (schedule?.next_midday && schedule?.next_evening) {
    const nm = new Date(schedule.next_midday).getTime();
    const ne = new Date(schedule.next_evening).getTime();
    const nmLeft = nm - now;
    const neLeft = ne - now;
    if (nmLeft > 0 && (nmLeft <= neLeft || neLeft <= 0)) {
      nextDraw = { kind: 'midday', at: nm, msLeft: nmLeft };
    } else if (neLeft > 0) {
      nextDraw = { kind: 'evening', at: ne, msLeft: neLeft };
    }
  }
  const isUrgent = nextDraw && nextDraw.msLeft < 15 * 60 * 1000;

  // Bubble urgency changes so parents (e.g. Home) can suppress overlays that
  // would block the CTA at the exact moment we're asking users to act.
  useEffect(() => {
    if (typeof onUrgentChange === 'function') onUrgentChange(!!isUrgent);
  }, [isUrgent, onUrgentChange]);

  const handleQuickBuy = () => {
    if (typeof onQuickBuy === 'function') {
      onQuickBuy(nextDraw);
      return;
    }
    // Default: scroll to the XRP Pool Rooms section.
    const target = document.querySelector('[data-testid="xrp-rooms-heading"]');
    if (target && target.scrollIntoView) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-2">
    <div
      data-testid="fireball-latest"
      className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-orange-500/15 via-amber-500/10 to-red-500/5 p-3 flex items-center gap-3 relative overflow-hidden"
    >
      {/* Subtle animated flame accent in the corner */}
      <div className="absolute -right-2 -top-2 opacity-10 pointer-events-none">
        <Flame className="w-16 h-16 text-orange-400" strokeWidth={1} />
      </div>

      {/* Left: big digit */}
      <div className="flex-shrink-0 relative">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 via-amber-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
          {loading ? (
            <RefreshCw className="w-6 h-6 text-white animate-spin" />
          ) : digit != null ? (
            <span data-testid="fireball-digit" className="text-4xl font-black text-white">{digit}</span>
          ) : (
            <span className="text-2xl font-bold text-white/60">?</span>
          )}
        </div>
        {refreshing && (
          <RefreshCw className="absolute -top-1 -right-1 w-3.5 h-3.5 text-amber-300 animate-spin bg-slate-900 rounded-full p-0.5" />
        )}
      </div>

      {/* Right: metadata */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <Flame className="w-3.5 h-3.5 text-orange-400" />
          <p className="text-[11px] font-bold text-orange-300 uppercase tracking-wide">
            Latest Fireball
          </p>
        </div>
        {latest ? (
          <>
            <p className="text-sm text-white font-semibold mt-0.5 flex items-center gap-1.5">
              {isMidday ? (
                <Sun className="w-3.5 h-3.5 text-yellow-300 flex-shrink-0" />
              ) : (
                <Moon className="w-3.5 h-3.5 text-indigo-300 flex-shrink-0" />
              )}
              {isMidday ? 'Midday draw' : 'Evening draw'}
              <span className="text-slate-400 font-normal">· {formatDate(latest.draw_date)}</span>
            </p>
            {nextDraw ? (
              <p
                data-testid="fireball-countdown"
                className={`text-[10px] mt-0.5 flex items-center gap-1 leading-snug ${
                  isUrgent ? 'text-amber-300 font-semibold animate-pulse' : 'text-slate-400'
                }`}
              >
                <Clock className="w-3 h-3 flex-shrink-0" />
                Next {nextDraw.kind} in{' '}
                <span className={isUrgent ? 'text-amber-200 font-black' : 'text-white font-semibold'}>
                  {formatCountdown(nextDraw.msLeft)}
                </span>
                <span className="text-slate-500">— buy in before midnight CT</span>
              </p>
            ) : (
              <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                Own slot <span className="text-amber-300 font-semibold">{digit}</span> at the next draw to win.
              </p>
            )}
          </>
        ) : (
          <p className="text-[11px] text-slate-400 mt-0.5">
            {loading ? 'Loading latest draw…' : 'No completed draws yet — first one lands after midday CT.'}
          </p>
        )}
      </div>
    </div>
    {isUrgent && nextDraw && (
      <button
        type="button"
        onClick={handleQuickBuy}
        data-testid="fireball-autobuy-cta"
        className="w-full flex items-center justify-between gap-3 rounded-2xl border border-amber-400/50 bg-gradient-to-r from-amber-500/25 via-orange-500/20 to-red-500/25 px-4 py-3 text-left shadow-lg shadow-amber-500/10 hover:shadow-amber-500/25 hover:border-amber-300 active:scale-[0.99] transition-all animate-pulse"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 flex-shrink-0 rounded-xl bg-amber-400/20 border border-amber-300/40 flex items-center justify-center">
            <Zap className="w-4 h-4 text-amber-200" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-bold text-amber-100 leading-tight">
              Grab a slot before the {nextDraw.kind} draw closes
            </p>
            <p className="text-[11px] text-amber-200/80 leading-tight mt-0.5">
              Only <span className="font-black text-white">{formatCountdown(nextDraw.msLeft)}</span> left — pick your number now
            </p>
          </div>
        </div>
        <span className="flex-shrink-0 text-[11px] font-bold text-slate-900 bg-amber-300 px-2.5 py-1.5 rounded-lg">
          Buy in
        </span>
      </button>
    )}
    </div>
  );
};

export default FireballLatest;
